import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Chip, Input, Spinner } from '@heroui/react';
import clsx from 'clsx';

import { usePatternManifest } from '~/hooks/useDesignPatterns';
import { useRefArchDetail } from '~/hooks/useReferenceArchitectures';

import type { PatternDomain, PatternManifestEntry, PatternSeverity } from '~/types';

import { PatternCard } from './components/PatternCard';
import { PatternContent } from './components/PatternContent';
import { RefArchBreadcrumb } from './components/RefArchBreadcrumb';
import {
  ARCHITECTURE_TYPE_LABELS,
  DOMAIN_ORDER,
  filterPatterns,
  formatDomainLabel,
  formatRefArchTitle,
  groupByDomain,
} from './utils';

type RefArchTab = 'architecture' | 'patterns';

const QUALITY_COLORS: Record<string, 'danger' | 'success' | 'warning' | 'default'> = {
  critical: 'danger',
  high: 'success',
  moderate: 'warning',
  low: 'default',
};

const SEVERITY_OPTIONS: Array<{
  color: 'danger' | 'primary' | 'default';
  label: string;
  value: PatternSeverity;
}> = [
  { value: 'critical', label: 'Critical', color: 'danger' },
  { value: 'recommended', label: 'Recommended', color: 'primary' },
  { value: 'advisory', label: 'Advisory', color: 'default' },
];

export function RefArchPage(): JSX.Element {
  const { refArchId } = useParams<{ refArchId: string }>();
  const { error, isLoading, refArch } = useRefArchDetail(refArchId);
  const { manifest: patternManifest } = usePatternManifest();
  const [activeTab, setActiveTab] = useState<RefArchTab>('architecture');
  const [brownfieldOpen, setBrownfieldOpen] = useState(false);

  // Local filter state for the Design Patterns tab
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomains, setSelectedDomains] = useState<Set<PatternDomain>>(new Set());
  const [selectedSeverities, setSelectedSeverities] = useState<Set<PatternSeverity>>(new Set());
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(new Set());

  // Reset tab and filters when navigating to a different architecture
  useEffect(() => {
    setActiveTab('architecture');
    setSearchQuery('');
    setSelectedDomains(new Set());
    setSelectedSeverities(new Set());
    setCollapsedDomains(new Set());
  }, [refArchId]);

  const relatedPatterns = useMemo(() => {
    if (!refArch || !patternManifest) return [];
    const idSet = new Set(refArch.pattern_ids);
    return patternManifest.patterns.filter(p => idSet.has(p.pattern_id));
  }, [refArch, patternManifest]);

  const filteredPatterns = useMemo(
    () => filterPatterns(relatedPatterns, { searchQuery, selectedDomains, selectedSeverities }),
    [relatedPatterns, searchQuery, selectedDomains, selectedSeverities],
  );

  const groupedPatterns = useMemo(() => groupByDomain(filteredPatterns), [filteredPatterns]);

  const toggleDomain = useCallback((domain: PatternDomain) => {
    setSelectedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  }, []);

  const toggleSeverity = useCallback((severity: PatternSeverity) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev);
      if (next.has(severity)) {
        next.delete(severity);
      } else {
        next.add(severity);
      }
      return next;
    });
  }, []);

  const toggleCollapsed = useCallback((domain: string) => {
    setCollapsedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedDomains(new Set());
    setSelectedSeverities(new Set());
  }, []);

  const hasActiveFilters = searchQuery || selectedDomains.size > 0 || selectedSeverities.size > 0;

  // Only show domains that exist among related patterns, in preferred order
  const availableDomains = useMemo(() => {
    const present = new Set(relatedPatterns.map(p => p.domain));
    const ordered = DOMAIN_ORDER.filter(d => present.has(d));
    for (const d of [...present].sort()) {
      if (!DOMAIN_ORDER.includes(d)) ordered.push(d);
    }
    return ordered;
  }, [relatedPatterns]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !refArch) {
    return (
      <div className="py-20 text-center text-error">
        {error ? 'Failed to load reference architecture.' : 'Reference architecture not found.'}
      </div>
    );
  }

  const tabs: Array<{ key: RefArchTab; label: string }> = [
    { key: 'architecture', label: 'Architecture' },
    { key: 'patterns', label: `Design Patterns (${relatedPatterns.length})` },
  ];

  return (
    <div>
      <RefArchBreadcrumb architectureType={refArch.architecture_type} variant={refArch.variant} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-3">
          {formatRefArchTitle(refArch.variant)}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Chip color="secondary" variant="flat">
            {ARCHITECTURE_TYPE_LABELS[refArch.architecture_type] ?? refArch.architecture_type}
          </Chip>
        </div>
        <p className="text-muted">{refArch.description}</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b border-default mb-6">
        {tabs.map(tab => {
          const active = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                px-5 py-2.5 font-semibold rounded-t-md transition-all cursor-pointer
                border-b-2 -mb-px
                ${
                  active
                    ? 'border-primary text-primary bg-primary/10'
                    : 'border-transparent text-muted hover:text-foreground hover:bg-default/40'
                }
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Architecture tab */}
      {activeTab === 'architecture' && (
        <>
          {/* Quality Profile */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-3">Quality Profile</h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(refArch.quality_profile).map(([attr, level]) => (
                <Chip key={attr} variant="flat" color={QUALITY_COLORS[level] ?? 'default'}>
                  {attr}: {level}
                </Chip>
              ))}
            </div>
          </div>

          {/* Markdown content */}
          <PatternContent content={refArch.content} />

          {/* Concerns */}
          {refArch.concerns.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-foreground mb-3">Concerns</h2>
              <div className="flex flex-wrap gap-2">
                {refArch.concerns.map(concern => (
                  <Chip key={concern} size="sm" variant="bordered">
                    {concern}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          {/* Brownfield Indicators */}
          {refArch.brownfield_indicators.length > 0 && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setBrownfieldOpen(!brownfieldOpen)}
                className="flex items-center gap-2 group"
              >
                <ChevronDownIcon
                  className={clsx('h-4 w-4 text-muted transition-transform', {
                    '-rotate-90': !brownfieldOpen,
                  })}
                />
                <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                  Brownfield Indicators
                </h2>
              </button>
              {brownfieldOpen && (
                <ul className="mt-2 ml-6 list-disc space-y-1 text-sm text-foreground">
                  {refArch.brownfield_indicators.map(indicator => (
                    <li key={indicator}>{indicator}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Design Patterns tab */}
      {activeTab === 'patterns' && (
        <div>
          {/* Filters */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search patterns..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                startContent={<MagnifyingGlassIcon className="h-4 w-4 text-muted" />}
                size="sm"
                className="max-w-xs"
                isClearable
                onClear={() => setSearchQuery('')}
              />
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="light"
                  onPress={resetFilters}
                  startContent={<XMarkIcon className="h-4 w-4" />}
                >
                  Clear all
                </Button>
              )}
              <span className="text-xs text-muted ml-auto">
                {filteredPatterns.length} of {relatedPatterns.length} patterns
              </span>
            </div>

            {/* Domain chips */}
            {availableDomains.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {availableDomains.map(domain => (
                  <Chip
                    key={domain}
                    size="sm"
                    variant={selectedDomains.has(domain) ? 'solid' : 'bordered'}
                    color={selectedDomains.has(domain) ? 'primary' : 'default'}
                    className="cursor-pointer"
                    onClick={() => toggleDomain(domain)}
                  >
                    {formatDomainLabel(domain)}
                  </Chip>
                ))}
              </div>
            )}

            {/* Severity chips */}
            <div className="flex flex-wrap gap-1.5">
              {SEVERITY_OPTIONS.map(({ color, label, value }) => (
                <Chip
                  key={value}
                  size="sm"
                  variant={selectedSeverities.has(value) ? 'solid' : 'bordered'}
                  color={selectedSeverities.has(value) ? color : 'default'}
                  className="cursor-pointer"
                  onClick={() => toggleSeverity(value)}
                >
                  {label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Pattern list grouped by domain */}
          {filteredPatterns.length === 0 ? (
            <div className="py-12 text-center text-muted">
              No patterns match your filters. Try adjusting your search or clearing filters.
            </div>
          ) : (
            <div className="space-y-6">
              {Array.from(groupedPatterns.entries()).map(
                ([domain, patterns]: [PatternDomain, PatternManifestEntry[]]) => {
                  const isOpen = !collapsedDomains.has(domain);
                  return (
                    <section key={domain}>
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(domain)}
                        className="flex items-center gap-2 mb-3 group"
                      >
                        <ChevronDownIcon
                          className={clsx('h-4 w-4 text-muted transition-transform', {
                            '-rotate-90': !isOpen,
                          })}
                        />
                        <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {formatDomainLabel(domain)}
                        </h2>
                        <span className="text-xs text-muted">({patterns.length})</span>
                      </button>
                      {isOpen && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {patterns.map(p => (
                            <PatternCard key={p.pattern_id} pattern={p} />
                          ))}
                        </div>
                      )}
                    </section>
                  );
                },
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
